import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dropdown, Option, Switch, Button, Spinner } from '@fluentui/react-components';
import { CardSkeleton } from '../components/LoadingSkeleton';
import {
  DocumentRegular,
  DocumentCheckmarkRegular,
  ClockRegular,
  WarningRegular,
  PenRegular,
  MoneyRegular,
  ArrowSyncRegular,
  ClipboardTaskListLtrRegular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { getDashboardStats, syncXeroPayments } from '../services/api';
import { statusDisplayNames, type DashboardStats } from '../types';
import { LastFetched } from '../components/LastFetched';
import { EFilePendingSection } from '../components/EFilePendingSection';
import { useResolvedPreparerId } from '../hooks/useResolvedPreparerId';
import { useNotificationToast } from '../hooks/useNotificationToast';

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1; // Tax prep year (preparing prior year's taxes)
const yearOptions = [defaultTaxYear, defaultTaxYear - 1, defaultTaxYear - 2];

export default function DashboardPage() {
  const [selectedYear, setSelectedYear] = useState(defaultTaxYear);
  const [showAllReturns, setShowAllReturns] = useState(false);
  const { resolvedPreparerId, isAdmin, isResolving } = useResolvedPreparerId(selectedYear);
  const queryClient = useQueryClient();
  const { showToast } = useNotificationToast();

  const syncMutation = useMutation({
    mutationFn: syncXeroPayments,
    onSuccess: (data) => {
      const parts = [];
      if (data.checked > 0) parts.push(`${data.checked} individual checked`);
      if (data.updatedToPaid > 0) parts.push(`${data.updatedToPaid} paid`);
      if (data.invoicesVoided && data.invoicesVoided > 0) parts.push(`${data.invoicesVoided} voided`);
      if (data.autoCompleted > 0) parts.push(`${data.autoCompleted} auto-completed`);
      if (data.sharedInvoicesChecked > 0) parts.push(`${data.sharedInvoicesChecked} shared checked`);
      if (data.sharedInvoicesUpdated > 0) parts.push(`${data.sharedInvoicesUpdated} shared paid`);
      if (data.preparerBillsChecked > 0) parts.push(`${data.preparerBillsChecked} preparer bills checked`);
      if (data.preparerBillsUpdated > 0) parts.push(`${data.preparerBillsUpdated} preparer bills paid`);
      if (data.errors > 0) parts.push(`${data.errors} errors`);
      const summary = parts.length > 0 ? parts.join(', ') : 'No invoices to sync';
      showToast('Xero Sync Complete', summary, data.errors > 0 ? 'warning' : 'success');
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
    },
    onError: (error: Error) => {
      showToast('Sync Failed', error.message, 'error');
    },
  });

  // Only pass preparerId when not showing all returns
  const effectivePreparerId = showAllReturns ? undefined : resolvedPreparerId;

  const { data: stats, isLoading, error, dataUpdatedAt, isFetching, refetch } = useQuery<DashboardStats>({
    queryKey: ['dashboardStats', selectedYear, effectivePreparerId],
    queryFn: () => getDashboardStats(selectedYear, effectivePreparerId),
    enabled: !!resolvedPreparerId || showAllReturns,
  });

  if (isLoading || isResolving) {
    return <CardSkeleton />;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Failed to load dashboard data. Please try again.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>Dashboard</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <p style={{ margin: 0 }}>
                {showAllReturns ? 'All returns' : 'My returns'} — {selectedYear} tax year
              </p>
              <LastFetched
                dataUpdatedAt={dataUpdatedAt}
                isFetching={isFetching}
                onRefresh={() => refetch()}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {isAdmin && (
              <Button
                appearance="subtle"
                icon={syncMutation.isPending ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
                disabled={syncMutation.isPending}
                onClick={() => syncMutation.mutate()}
              >
                {syncMutation.isPending ? 'Syncing...' : 'Sync Xero Payments'}
              </Button>
            )}
            {isAdmin && (
              <Switch
                checked={showAllReturns}
                onChange={(_, data) => setShowAllReturns(data.checked)}
                label={showAllReturns ? 'All Returns' : 'My Returns'}
              />
            )}
            <Dropdown
              value={selectedYear.toString()}
              onOptionSelect={(_, data) => setSelectedYear(Number(data.optionValue))}
              style={{ minWidth: '120px' }}
            >
              {yearOptions.map((year) => (
                <Option key={year} text={`Tax Year ${year}`} value={year.toString()}>
                  Tax Year {year}
                </Option>
              ))}
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard
          icon={<DocumentRegular />}
          value={stats?.totalReturns || 0}
          label="Total Returns"
          color="#0078d4"
        />
        <StatCard
          icon={<ClockRegular />}
          value={stats?.pendingDocuments || 0}
          label="Pending Documents"
          color="#f59e0b"
        />
        <StatCard
          icon={<PenRegular />}
          value={stats?.pendingSignatures || 0}
          label="Pending Signatures"
          color="#ec4899"
        />
        <StatCard
          icon={<MoneyRegular />}
          value={stats?.pendingPayments || 0}
          label="Unpaid Invoices"
          color="#8b5cf6"
        />
        <StatCard
          icon={<WarningRegular />}
          value={stats?.efileRejections || 0}
          label="E-File Rejections"
          color="#ef4444"
        />
        <StatCard
          icon={<ClipboardTaskListLtrRegular />}
          value={stats?.unassignedDropoffs || 0}
          label="Drop-Off Pool"
          color="#f97316"
        />
        <StatCard
          icon={<DocumentCheckmarkRegular />}
          value={stats?.completedThisWeek || 0}
          label="Completed This Week"
          color="#10b981"
        />
      </div>

      <EFilePendingSection
        taxYear={selectedYear}
        preparerId={effectivePreparerId}
        enabled={!!resolvedPreparerId || showAllReturns}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Returns by Status</span>
          </div>
          <div>
            {stats?.returnsByStatus && Object.entries(stats.returnsByStatus).length > 0 ? (
              Object.entries(stats.returnsByStatus).map(([status, count]) => (
                <div key={status} className="detail-row">
                  <span className="detail-label">
                    {statusDisplayNames[status as keyof typeof statusDisplayNames] || status}
                  </span>
                  <span className="detail-value">{count}</span>
                </div>
              ))
            ) : (
              <p className="empty-state">No data available</p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Returns by Preparer</span>
          </div>
          <div>
            {stats?.returnsByPreparer && Object.entries(stats.returnsByPreparer).length > 0 ? (
              Object.entries(stats.returnsByPreparer).map(([preparer, count]) => (
                <div key={preparer} className="detail-row">
                  <span className="detail-label">{preparer}</span>
                  <span className="detail-value">{count}</span>
                </div>
              ))
            ) : (
              <p className="empty-state">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  color: string;
}

function StatCard({ icon, value, label, color }: StatCardProps) {
  return (
    <div className="stat-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <div style={{ color }}>{icon}</div>
      </div>
      <span className="stat-value" style={{ color }}>{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}
